import React from 'react';
import { 
  Type, 
  Image, 
  Square, 
  Minus, 
  MoveVertical, 
  Share2, 
  Code, 
  LayoutGrid,
  Columns,
  PanelTop,
  PanelBottom
} from 'lucide-react';
import { EmailBlock } from '../types';
import { ComponentItem } from './ComponentItem';

interface ComponentLibraryProps {
  onDragStart: (blockType: EmailBlock['type']) => void;
  onDragEnd: () => void;
  isDarkMode: boolean;
}

const COMPONENT_ITEMS = [
  {
    type: 'text' as const,
    name: 'Text',
    icon: Type,
    category: 'content' as const,
    description: 'Add text content'
  },
  {
    type: 'image' as const,
    name: 'Image',
    icon: Image,
    category: 'content' as const,
    description: 'Add an image'
  },
  {
    type: 'button' as const,
    name: 'Button',
    icon: Square,
    category: 'content' as const,
    description: 'Add a button'
  },
  {
    type: 'divider' as const,
    name: 'Divider',
    icon: Minus,
    category: 'content' as const,
    description: 'Add a divider line'
  },
  {
    type: 'spacer' as const,
    name: 'Spacer',
    icon: MoveVertical,
    category: 'content' as const,
    description: 'Add spacing'
  },
  {
    type: 'social' as const,
    name: 'Social',
    icon: Share2,
    category: 'content' as const,
    description: 'Add social media icons'
  },
  {
    type: 'html' as const,
    name: 'HTML',
    icon: Code,
    category: 'advanced' as const,
    description: 'Add custom HTML'
  },
  {
    type: 'row' as const,
    name: 'Row',
    icon: LayoutGrid,
    category: 'layout' as const,
    description: 'Add a row container'
  },
  {
    type: 'column' as const,
    name: 'Column',
    icon: Columns,
    category: 'layout' as const,
    description: 'Add a column container'
  },
  {
    type: 'header' as const,
    name: 'Header',
    icon: PanelTop,
    category: 'layout' as const,
    description: 'Add email header with logo and navigation'
  },
  {
    type: 'footer' as const,
    name: 'Footer',
    icon: PanelBottom,
    category: 'layout' as const,
    description: 'Add email footer with subscription management'
  }
];

export const ComponentLibrary: React.FC<ComponentLibraryProps> = ({
  onDragStart,
  onDragEnd,
  isDarkMode
}) => {
  const contentItems = COMPONENT_ITEMS.filter(item => item.category === 'content');
  const layoutItems = COMPONENT_ITEMS.filter(item => item.category === 'layout');
  const advancedItems = COMPONENT_ITEMS.filter(item => item.category === 'advanced');

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Components
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Drag to add to email
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {/* Content Components */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
            Content
          </h4>
          <div className="space-y-1">
            {contentItems.map(item => (
              <ComponentItem
                key={item.type}
                type={item.type}
                name={item.name}
                icon={item.icon}
                description={item.description}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>

        {/* Layout Components */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
            Layout
          </h4>
          <div className="space-y-1">
            {layoutItems.map(item => (
              <ComponentItem
                key={item.type}
                type={item.type}
                name={item.name}
                icon={item.icon}
                description={item.description}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>

        {/* Advanced Components */}
        {advancedItems.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
              Advanced
            </h4>
            <div className="space-y-1">
              {advancedItems.map(item => (
                <ComponentItem
                  key={item.type}
                  type={item.type}
                  name={item.name}
                  icon={item.icon}
                  description={item.description}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
